import { Component, useState, useRef, useEffect, type ReactNode, type ErrorInfo } from 'react';
import { useCoachOptIn } from '@/hooks/useCoachOptIn';
import { useProfile } from '@/hooks/useProfile';
import { useGoalsDB } from '@/hooks/useGoalsDB';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnhancedCoachingOptInModal } from './EnhancedCoachingOptInModal';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, Send, Loader2, Sparkles, User, RotateCcw, Settings, UserCircle } from 'lucide-react';
import { CoachPanelHeader } from './CoachPanelHeader';
import { CoachQuickStarts } from './CoachQuickStarts';
import { CoachConversation } from './CoachConversation';
import type { AnalysisType } from './CoachConversation';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';
import ReactMarkdown from 'react-markdown';
import { EType } from '@/types/goal';

/** Compact error boundary that keeps the Drawer shell functional */
class CoachPanelErrorBoundary extends Component<
  { children: ReactNode; onReset: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; onReset: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError(error, 'ai-coach', { component: 'CoachPanel', componentStack: info.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-12 px-4 text-center">
          <AlertTriangle className="w-8 h-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Something went wrong</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              this.setState({ hasError: false });
              this.props.onReset();
            }}
          >
            Try Again
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface CoachPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export const CoachPanel = ({ open, onOpenChange }: CoachPanelProps) => {
  const { user } = useAuth();
  const { profile, invalidate: invalidateProfile } = useProfile();
  const { goals, addGoal, addHabit } = useGoalsDB();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const { guardCoachAction, modalOpen, setModalOpen } = useCoachOptIn();
  const { toast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Career path confirmation dialog state
  const [careerPathConfirm, setCareerPathConfirm] = useState<{ title: string } | null>(null);
  const [careerPathUpdating, setCareerPathUpdating] = useState(false);

  // Load previous conversation from DB on first open
  useEffect(() => {
    if (!open || historyLoaded || !user) return;
    (async () => {
      try {
        const { data } = await supabase
          .from('career_coach_analyses')
          .select('data')
          .eq('user_id', user.id)
          .eq('analysis_type', 'freetext_chat')
          .maybeSingle();
        if (data?.data?.messages?.length) {
          setChatMessages(data.data.messages as ChatMessage[]);
        }
      } catch (err) {
        // Non-critical — just start with empty chat
        reportError(err, 'ai-coach', { action: 'load-chat-history' });
      } finally {
        setHistoryLoaded(true);
      }
    })();
  }, [open, historyLoaded, user]);

  // Auto-focus the input when the panel opens
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Scroll to bottom when new messages arrive or drawer opens
  useEffect(() => {
    if (!open) return;
    const scrollToBottom = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    };
    // Immediate for new messages while drawer is already open
    scrollToBottom();
    // After drawer open animation completes (~300ms) for re-open case
    const timer = setTimeout(scrollToBottom, 350);
    return () => clearTimeout(timer);
  }, [open, chatMessages, chatLoading]);

  const handleClose = () => {
    onOpenChange(false);
    setSelectedType(null);
    setInputValue('');
  };

  const handleNewChat = async () => {
    setChatMessages([]);
    setSelectedType(null);
    setInputValue('');
    // Clear persisted conversation
    if (user) {
      try {
        await supabase
          .from('career_coach_analyses')
          .delete()
          .eq('user_id', user.id)
          .eq('analysis_type', 'freetext_chat');
        queryClient.invalidateQueries({ queryKey: ['coach-panel-last'] });
      } catch {
        // Non-critical
      }
    }
  };

  const handleSubmitMessage = async () => {
    if (!inputValue.trim() || chatLoading) return;
    if (!guardCoachAction()) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    const prevMessages = [...chatMessages];
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/career-coach`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            type: 'freetext_chat',
            message: userMessage,
            history: prevMessages,
            jobRole: profile?.job_role || undefined,
            targetJobRole: profile?.target_job_role || undefined,
            skills: profile?.skills || [],
            goals: goals?.map(g => ({
              title: g.title,
              progress: g.progress,
              e_types: g.eTypes,
            })),
          }),
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (response.status === 429 && errData.retry_after_seconds) {
          const sec = errData.retry_after_seconds;
          const timeMsg = sec >= 60 ? `${Math.ceil(sec / 60)} minute(s)` : `${sec} seconds`;
          throw new Error(`Too many requests. Please try again in ${timeMsg}.`);
        }
        throw new Error(errData.error || 'Failed to get response');
      }

      const data = await response.json();
      const aiResponse = data.data?.response || 'Sorry, I couldn\'t generate a response.';
      setChatMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      // Refresh the "Last time" header
      queryClient.invalidateQueries({ queryKey: ['coach-panel-last'] });
    } catch (err) {
      reportError(err, 'ai-coach', { action: 'freetext-chat' });
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `I'm sorry, I encountered an error: ${err instanceof Error ? err.message : 'Please try again.'}`,
      }]);
    } finally {
      setChatLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // --- Action handlers for CoachConversation result components ---

  const handleAddGoalFromCoach = async (title: string, description: string, eType: string) => {
    const normalizedEType = eType.toLowerCase() as EType;
    const validETypes: EType[] = ['education', 'exposure', 'experience'];
    const selectedEType = validETypes.includes(normalizedEType) ? normalizedEType : 'education';
    const result = await addGoal(title, description, [selectedEType]);
    if (result) {
      toast({ title: 'Goal added!', description: `"${title}" has been added to your goals.` });
    }
  };

  const handleAddHabitFromCoach = async (goalId: string, title: string) => {
    await addHabit(goalId, title);
    toast({ title: 'Habit added!', description: `"${title}" has been added to your habits.` });
  };

  const handleSelectCareerPath = (title: string) => {
    setCareerPathConfirm({ title });
  };

  const handleConfirmCareerPath = async () => {
    if (!careerPathConfirm || !user) return;
    setCareerPathUpdating(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ target_job_role: careerPathConfirm.title })
        .eq('user_id', user.id);
      if (error) throw error;
      invalidateProfile();
      toast({
        title: 'Target role updated!',
        description: `Your target role has been set to "${careerPathConfirm.title}".`,
      });
    } catch (err) {
      reportError(err, 'ai-coach', { action: 'set-career-path' });
      toast({
        title: 'Failed to update',
        description: 'Could not update your target role. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCareerPathUpdating(false);
      setCareerPathConfirm(null);
    }
  };

  const handleSetTargetRoleManually = () => {
    navigate('/settings');
    handleClose();
  };

  const inChatMode = chatMessages.length > 0;
  const missingProfile = !profile?.job_role;

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      onClose={handleClose}
      modal
    >
      <DrawerContent className="max-h-[75vh]">
        <DrawerHeader className="sr-only">
          <DrawerTitle>AI Coach</DrawerTitle>
        </DrawerHeader>
        <CoachPanelErrorBoundary onReset={() => { setSelectedType(null); setChatMessages([]); }}>
          <div className="flex flex-col min-h-0 flex-1">
          <div className="flex items-start justify-between">
            <CoachPanelHeader />
            {inChatMode && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="mt-4 mr-4 shrink-0 text-xs text-muted-foreground"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                New Chat
              </Button>
            )}
          </div>

          {/* Profile prompt when job_role is missing */}
          {missingProfile && !inChatMode && (
            <div className="mx-4 mb-3 p-3 rounded-lg border border-primary/20 bg-primary/5 flex items-center gap-3">
              <UserCircle className="w-5 h-5 text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm">Add your job role in Settings for personalized coaching</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="shrink-0 gap-1 text-xs"
                onClick={() => { navigate('/settings'); handleClose(); }}
              >
                <Settings className="w-3 h-3" />
                Settings
              </Button>
            </div>
          )}

          {/* Quick starts — hide once a chat or analysis is active */}
          {!inChatMode && (
            <CoachQuickStarts onSelect={(type) => {
              if (!guardCoachAction()) return;
              setSelectedType(type);
            }} />
          )}

          <div className="flex-1 min-h-0 overflow-y-auto" ref={scrollRef}>
            {/* Predefined analysis results */}
            {selectedType && !inChatMode && (
              <CoachConversation
                analysisType={selectedType as AnalysisType | null}
                onComplete={() => {}}
                onAddGoal={handleAddGoalFromCoach}
                onAddHabit={handleAddHabitFromCoach}
                onSelectCareerPath={handleSelectCareerPath}
                onSetTargetRoleManually={handleSetTargetRoleManually}
              />
            )}

            {/* Inline chat messages */}
            {inChatMode && (
              <div className="px-4 py-3 space-y-4">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {msg.role === 'assistant' && (
                      <div className="shrink-0 w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                        <Sparkles className="w-3.5 h-3.5 text-primary" />
                      </div>
                    )}
                    <div className={`rounded-lg px-3 py-2 max-w-[85%] text-sm leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}>
                      {msg.role === 'assistant' ? (
                        <div className="coach-markdown text-sm leading-relaxed [&>p]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&>ul]:my-1.5 [&>ul]:pl-4 [&>ul]:list-disc [&>ol]:my-1.5 [&>ol]:pl-4 [&>ol]:list-decimal [&>li]:my-0.5 [&_strong]:font-semibold [&>h3]:font-semibold [&>h3]:mt-3 [&>h3]:mb-1">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="shrink-0 w-7 h-7 rounded-full bg-muted flex items-center justify-center mt-0.5">
                        <User className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                ))}
                {chatLoading && (
                  <div className="flex gap-3 justify-start">
                    <div className="shrink-0 w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary animate-pulse" />
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty state */}
            {!selectedType && !inChatMode && (
              <div className="text-center text-sm text-muted-foreground py-8">
                Choose a quick start above to begin
              </div>
            )}
          </div>

          <div className="px-4 py-3 border-t border-border/50 bg-background">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                data-testid="coach-input"
                aria-label="Message your coach"
                placeholder="Ask your coach anything..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmitMessage();
                  }
                }}
                disabled={chatLoading}
                className="flex-1"
              />
              <Button
                size="icon"
                variant="default"
                onClick={handleSubmitMessage}
                disabled={!inputValue.trim() || chatLoading}
                aria-label="Send message"
                className="shrink-0"
              >
                {chatLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          </div>
        </CoachPanelErrorBoundary>
      </DrawerContent>

      {/* Career path confirmation dialog (portal renders above drawer) */}
      <AlertDialog open={!!careerPathConfirm} onOpenChange={(open) => { if (!open) setCareerPathConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Set target role?</AlertDialogTitle>
            <AlertDialogDescription>
              This will set your target job role to <strong>"{careerPathConfirm?.title}"</strong>. Your coach analyses and development plan will use this as your career target.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={careerPathUpdating}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCareerPath} disabled={careerPathUpdating}>
              {careerPathUpdating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EnhancedCoachingOptInModal open={modalOpen} onOpenChange={setModalOpen} />
    </Drawer>
  );
};
